import type { CarouselHeight, CarouselSpeed } from "./carousel";
import type { DashboardRecord, DashboardDefinition } from "./types";
import { isValidSectionWidth } from "./sectionWidth";

/** A JSON composition, independent of dashboard identity, slug and database row. */
export interface DashboardComposition {
  /**
   * Code of the image-library image shown beside the dashboard title (a map, a
   * logo, a flag, an icon…; see `availableLibraryImages`). Read it with
   * `dashboardHeaderImage`, write it with `withDashboardHeaderImage`.
   */
  headerImage?: string;
  /** @deprecated The former name of `headerImage`, still found on dashboards saved before the rename. */
  headerMap?: string;
  context?: {
    type: "event";
    protectedEvent?: boolean;
    selectFirstInTenant?: boolean;
    /** Optional links for the built-in event workspace. Otherwise selection stays on this page. */
    view?: "monitor" | "overview";
    basePath?: string;
    eventPath?: string;
    /**
     * Which parts of the business context the header states beside the title.
     * Absent shows the tenant only (the event and protected event are already
     * in the Event control); `[]` shows nothing.
     */
    show?: Array<"tenant" | "event" | "protected_event">;
  };
  blocks?: Array<{
    type: "widgets" | "event-comparison" | "campaign-schedule" | "event-protection";
    id?: string;
    title?: string;
    /** Omit to use the whole stored definition. An empty list intentionally renders none. */
    widgetInstanceIds?: string[];
    /** How a widgets section lays out its widgets. Absent is the ordinary grid. */
    layout?: "grid" | "carousel";
    /** Motion of a carousel section; see `lib/dashboard/carousel`. */
    carousel?: { speed?: CarouselSpeed; height?: CarouselHeight };
    /**
     * Columns of the page (1–12) a widgets section takes from `xl`, so sections
     * can sit side by side; absent is the full width. See `lib/dashboard/sectionWidth`.
     */
    width?: number;
    /** Code of the image-library image before the section title (a flag, a logo, an icon…). */
    image?: string;
    /** Size of the section title; absent is `standard`. `large` suits sections side by side. */
    heading?: "standard" | "large";
    showHeader?: boolean;
    /**
     * How the section stands apart: absent/`none` by space alone, `line` by a
     * hairline in the gap before it (beside or above), `panel` on a framed surface.
     */
    frame?: SectionFrame;
  }>;
  bodies?: DashboardBody[];
  pages?: DashboardPage[];
  pagePresentation?: DashboardPagePresentation;
  visualPreferences?: boolean;
  /**
   * The dashboard fills the screen, in the standard view and in full screen (war room):
   * rows with a height weight share the height left (see `useFitToScreen`). Absent is
   * off: the dashboard keeps its natural height and the page scrolls (reports, analysis).
   */
  fitToScreen?: boolean;
  /** Canonical domain links which should stay within a copied dashboard. */
  localRoutes?: string[];
  blockingPeriodSpotlights?: BlockingPeriodSpotlightRelationship[];
}

export type DashboardBlock = NonNullable<DashboardComposition["blocks"]>[number];

export type SectionFrame = "none" | "line" | "panel";

export interface DashboardBody {
  id: string;
  blocks: DashboardBlock[];
}

/** A page of the dashboard: a tab viewers switch to, or a step of the rotation. */
export interface DashboardPage {
  id: string;
  label: string;
  bodyId: string;
  showHeader?: boolean;
  /** Code of the image-library image beside the page title (an icon, a flag, a logo…). */
  image?: string;
}

/**
 * Page navigation: clickable tabs, the current page's header (`title`), a
 * vertical list of the pages beside the content (`rail`), or no visible
 * navigation during automatic rotation (`none`).
 */
export type DashboardPageNavigation = "tabs" | "title" | "rail" | "none";

/**
 * Whether the page's title (its name and image) is imposed by the navigation:
 * with several pages, Page header navigation *is* the page's title with the
 * previous / next controls, so a page cannot hide it there. Its own choice is
 * kept and applies again under another navigation.
 */
export function pageHeaderRequired(
  pageCount: number,
  navigation: DashboardPageNavigation = "title",
): boolean {
  return pageCount > 1 && navigation === "title";
}

/**
 * Whether the page shows its title above its content: always under Page header
 * navigation (see `pageHeaderRequired`), otherwise the page's own choice. A page
 * without one keeps the former default: a title beside a side list, none with
 * tabs (they already name it), hidden navigation or a single page.
 */
export function pageHeaderVisible(
  page: DashboardPage,
  pageCount: number,
  navigation: DashboardPageNavigation = "title",
): boolean {
  if (pageHeaderRequired(pageCount, navigation)) return true;
  return page.showHeader ?? (pageCount > 1 && navigation === "rail");
}

/**
 * How a page change is marked: `subtle` (absent) slides the new page in;
 * `announce` also shows the new page's image and name across the content for
 * a moment, so a room watching from a distance sees the subject change.
 */
export type DashboardPageTransition = "subtle" | "announce";

export interface DashboardPagePresentation {
  mode: "tabs" | "automatic";
  intervalSeconds?: number;
  /** Absent uses the legacy default: title in automatic mode, tabs in manual mode. */
  navigation?: DashboardPageNavigation;
  /** Absent is `subtle`. */
  transition?: DashboardPageTransition;
  /**
   * Where the page names itself outside full screen when its title shows (see
   * `pageTitlePlacement`): absent/`content`, a band above the content; `header`,
   * beside the dashboard's title in the page header, as full screen always does.
   */
  titlePlacement?: DashboardPageTitlePlacement;
  /**
   * Page colors (see `pageAccentMode`): absent/`page`, one accent per page by
   * position; `neutral`, one quiet ink for every page.
   */
  accent?: DashboardPageAccentMode;
  /**
   * The page controls — previous, pause with its dwell ring, next — on screen
   * (`shown`) or reachable by the keyboard and the More menu only (`hidden`), in
   * every navigation and in full screen. Absent keeps each navigation's former
   * look (see `pageControlsShown`).
   */
  controls?: DashboardPageControlsMode;
}

export type DashboardPageControlsMode = "shown" | "hidden";

/**
 * Whether the page controls are on screen. Where they go depends on the
 * navigation: the page band or the side list, the right end of the tabs, the
 * header's right before its filters (page title in the header, full screen,
 * hidden navigation). Shown, the next page's line drops its dwell pill: the
 * pause ring already fills over the dwell.
 *
 * Absent, a page band and a side list show them, as they always did; tabs,
 * hidden navigation and a page title in the header do not.
 */
export function pageControlsShown(presentation: DashboardPagePresentation): boolean {
  if (presentation.controls) return presentation.controls === "shown";
  const navigation = pageNavigation(presentation);
  return (
    navigation === "rail" ||
    (navigation === "title" && pageTitlePlacement(presentation) === "content")
  );
}

export type DashboardPageAccentMode = "page" | "neutral";

/**
 * How pages are colored where they have no artwork of their own: the tile of an
 * icon, a map or a page number, the band's edge, the side list's current page,
 * the pause ring and the announced page. Flags and logos keep their own colors.
 */
export function pageAccentMode(presentation: DashboardPagePresentation): DashboardPageAccentMode {
  return presentation.accent === "neutral" ? "neutral" : "page";
}

export type DashboardPageTitlePlacement = "content" | "header";

/**
 * Where the page's title (with its navigation line) goes outside full screen.
 * Full screen always puts it beside the dashboard's title; it applies whenever
 * a page header would show (Page header or Side list navigation, or a single
 * page showing its title), never to tabs or hidden navigation.
 */
export function pageTitlePlacement(
  presentation: DashboardPagePresentation,
): DashboardPageTitlePlacement {
  return presentation.titlePlacement === "header" ? "header" : "content";
}

export const PAGE_NAVIGATIONS: readonly DashboardPageNavigation[] = [
  "tabs",
  "title",
  "rail",
  "none",
];

/** Resolve independent navigation while preserving defaults of existing dashboards. */
export function pageNavigation(presentation: DashboardPagePresentation): DashboardPageNavigation {
  if (presentation.mode === "automatic") return presentation.navigation ?? "title";
  // Hidden navigation needs the rotation: a manual dashboard falls back to tabs.
  return presentation.navigation === "title" || presentation.navigation === "rail"
    ? presentation.navigation
    : "tabs";
}

export function pageTransition(presentation: DashboardPagePresentation): DashboardPageTransition {
  return presentation.transition === "announce" ? "announce" : "subtle";
}

export interface BlockingPeriodSpotlightRelationship {
  id: string;
  spotlightWidgetInstanceId: string;
  chartWidgetInstanceIds: string[];
}

export function removeSpotlightWidgetReferences(
  composition: DashboardComposition | undefined,
  instanceId: string,
): DashboardComposition | undefined {
  if (!composition?.blockingPeriodSpotlights) return composition;
  return {
    ...composition,
    blockingPeriodSpotlights: composition.blockingPeriodSpotlights
      .filter((link) => link.spotlightWidgetInstanceId !== instanceId)
      .map((link) => ({
        ...link,
        chartWidgetInstanceIds: link.chartWidgetInstanceIds.filter((id) => id !== instanceId),
      })),
  };
}

export function removeDashboardWidgetReferences(
  composition: DashboardComposition | undefined,
  instanceId: string,
): DashboardComposition | undefined {
  const cleaned = removeSpotlightWidgetReferences(composition, instanceId);
  if (!cleaned) return cleaned;
  const cleanBlocks = (blocks: DashboardBlock[]) =>
    blocks.map((block) => ({
      ...block,
      ...(block.widgetInstanceIds
        ? { widgetInstanceIds: block.widgetInstanceIds.filter((id) => id !== instanceId) }
        : {}),
    }));
  return {
    ...cleaned,
    ...(cleaned.blocks ? { blocks: cleanBlocks(cleaned.blocks) } : {}),
    ...(cleaned.bodies
      ? { bodies: cleaned.bodies.map((body) => ({ ...body, blocks: cleanBlocks(body.blocks) })) }
      : {}),
  };
}

export function copyDashboardConfig(
  config: DashboardRecord["config"],
  createInstanceId = () => `inst-${crypto.randomUUID()}`,
): DashboardRecord["config"] {
  const widgets = [
    ...(config.widgets ?? []),
    ...(config.sections?.flatMap((section) => section.widgets) ?? []),
  ];
  const identifiers = new Map(widgets.map((widget) => [widget.instanceId, createInstanceId()]));
  const remap = (id: string) => identifiers.get(id) ?? id;
  const bodyIdentifiers = new Map(
    config.composition?.bodies?.map((body) => [body.id, createInstanceId()]),
  );
  return {
    ...config,
    widgets: config.widgets?.map((widget) => ({ ...widget, instanceId: remap(widget.instanceId) })),
    sections: config.sections?.map((section) => ({
      ...section,
      widgets: section.widgets.map((widget) => ({
        ...widget,
        instanceId: remap(widget.instanceId),
      })),
      summaryInstanceId: section.summaryInstanceId ? remap(section.summaryInstanceId) : undefined,
    })),
    composition: config.composition
      ? {
          ...config.composition,
          blocks: config.composition.blocks?.map((block) => ({
            ...block,
            widgetInstanceIds: block.widgetInstanceIds?.map(remap),
          })),
          bodies: config.composition.bodies?.map((body) => ({
            ...body,
            id: bodyIdentifiers.get(body.id)!,
            blocks: body.blocks.map((block) => ({
              ...block,
              widgetInstanceIds: block.widgetInstanceIds?.map(remap),
            })),
          })),
          pages: config.composition.pages?.map((page) => ({
            ...page,
            id: createInstanceId(),
            bodyId: bodyIdentifiers.get(page.bodyId) ?? page.bodyId,
          })),
          blockingPeriodSpotlights: config.composition.blockingPeriodSpotlights?.map((link) => ({
            id: `blocking-period:${remap(link.spotlightWidgetInstanceId)}`,
            spotlightWidgetInstanceId: remap(link.spotlightWidgetInstanceId),
            chartWidgetInstanceIds: link.chartWidgetInstanceIds.map(remap),
          })),
        }
      : undefined,
  };
}

const widgetBlocks: NonNullable<DashboardComposition["blocks"]> = [{ type: "widgets" }];

export function resolveDashboardPages(composition: DashboardComposition = {}) {
  if (!composition.pages) {
    return {
      explicit: false,
      pages: [{ id: "legacy", label: "Dashboard", bodyId: "legacy" }] as DashboardPage[],
      bodies: [{ id: "legacy", blocks: composition.blocks ?? widgetBlocks }],
      presentation: { mode: "tabs" } as DashboardPagePresentation,
    };
  }
  return {
    explicit: true,
    pages: composition.pages,
    bodies: composition.bodies ?? [],
    presentation: composition.pagePresentation ?? { mode: "tabs" as const },
  };
}

export function validateDashboardPages(config: DashboardRecord["config"]): string[] {
  const composition = config.composition;
  if (!composition) return [];
  const { pages, bodies } = composition;
  if (!pages && !bodies) {
    return composition.pagePresentation ? ["composition.pagePresentation requires pages"] : [];
  }
  if (!pages?.length || !bodies?.length) {
    return ["composition.pages and bodies must be nonempty and provided together"];
  }
  const errors: string[] = [];
  const validId = (value: string) => value.trim().length > 0 && value === value.trim();
  const bodyIds = new Set(bodies.map((body) => body.id));
  if (bodyIds.size !== bodies.length || bodies.some((body) => !validId(body.id))) {
    errors.push("composition.bodies IDs must be nonempty and unique");
  }
  if (
    new Set(pages.map((page) => page.id)).size !== pages.length ||
    pages.some((page) => !validId(page.id) || !page.label.trim())
  ) {
    errors.push("composition.pages IDs must be nonempty and unique with nonempty labels");
  }
  for (const page of pages) {
    if (!bodyIds.has(page.bodyId))
      errors.push(`composition.pages.${page.id}.bodyId references a missing body`);
  }
  const presentation = composition.pagePresentation;
  if (
    presentation &&
    (!["tabs", "automatic"].includes(presentation.mode) ||
      (presentation.intervalSeconds !== undefined &&
        (!Number.isInteger(presentation.intervalSeconds) ||
          presentation.intervalSeconds < 5 ||
          presentation.intervalSeconds > 3600)) ||
      (presentation.navigation !== undefined &&
        !PAGE_NAVIGATIONS.includes(presentation.navigation)) ||
      (presentation.transition !== undefined &&
        !["subtle", "announce"].includes(presentation.transition)) ||
      (presentation.titlePlacement !== undefined &&
        !["content", "header"].includes(presentation.titlePlacement)) ||
      (presentation.accent !== undefined && !["page", "neutral"].includes(presentation.accent)) ||
      (presentation.controls !== undefined && !["shown", "hidden"].includes(presentation.controls)))
  ) {
    errors.push(
      "composition.pagePresentation requires a valid mode, navigation, transition, title placement, page colors, controls and dwell of 5-3600 seconds",
    );
  }
  const blocks = composition.blocks;
  if (
    blocks?.length &&
    (blocks.length !== 1 ||
      blocks[0].type !== "widgets" ||
      Object.keys(blocks[0]).some((key) => key !== "type"))
  ) {
    errors.push("composition.blocks cannot accompany explicit bodies");
  }
  const identifiers = new Set([
    ...(config.widgets ?? []).map((widget) => widget.instanceId),
    ...(config.sections ?? []).flatMap((section) =>
      section.widgets.map((widget) => widget.instanceId),
    ),
  ]);
  for (const body of bodies) {
    if (!body.blocks.length)
      errors.push(`composition.bodies.${body.id} requires at least one block`);
    for (const block of body.blocks) {
      if (block.heading !== undefined && !["standard", "large"].includes(block.heading))
        errors.push(`composition.bodies.${body.id} section headings are standard or large`);
      if (
        block.frame !== undefined &&
        (block.type !== "widgets" || !["none", "line", "panel"].includes(block.frame))
      )
        errors.push(`composition.bodies.${body.id} section frames are none, line or panel`);
      if (block.width === undefined) continue;
      if (block.type !== "widgets" || !isValidSectionWidth(block.width))
        errors.push(
          `composition.bodies.${body.id} section widths must be whole columns from 1 to 12, on widget sections`,
        );
    }
    const blockIds = body.blocks.flatMap((block) => (block.id === undefined ? [] : [block.id]));
    if (new Set(blockIds).size !== blockIds.length || blockIds.some((id) => !validId(id))) {
      errors.push(`composition.bodies.${body.id} block IDs must be nonempty and unique`);
    }
    const members = new Set<string>();
    for (const block of body.blocks) {
      if (block.type !== "widgets" && block.type !== "event-protection") {
        if (block.widgetInstanceIds !== undefined)
          errors.push(`composition.bodies.${body.id} domain blocks cannot claim widgets`);
        continue;
      }
      if (!block.widgetInstanceIds)
        errors.push(`composition.bodies.${body.id} requires explicit widgetInstanceIds`);
      for (const id of block.widgetInstanceIds ?? []) {
        if (!identifiers.has(id))
          errors.push(`composition.bodies.${body.id} references missing widget ${id}`);
        if (members.has(id)) errors.push(`composition.bodies.${body.id} repeats widget ${id}`);
        members.add(id);
      }
    }
    for (const link of composition.blockingPeriodSpotlights ?? []) {
      if (
        link.chartWidgetInstanceIds.some(
          (id) => members.has(id) !== members.has(link.spotlightWidgetInstanceId),
        )
      ) {
        errors.push(`composition.bodies.${body.id} splits blocking period relationship ${link.id}`);
      }
    }
  }
  return errors;
}

export function removeDashboardPage(
  composition: DashboardComposition,
  pageId: string,
): DashboardComposition {
  const pages = composition.pages?.filter((page) => page.id !== pageId);
  if (!pages?.length) throw new Error("A dashboard must retain at least one page");
  return { ...composition, pages: pages };
}

export function enableDashboardPages(
  config: DashboardRecord["config"],
  createId: () => string = () => crypto.randomUUID(),
): DashboardRecord["config"] {
  if (config.composition?.pages) return config;
  const composition = config.composition ?? {};
  const blocks = composition.blocks ?? [{ type: "widgets" as const }];
  const identifiers = [
    ...(config.widgets ?? []).map((widget) => widget.instanceId),
    ...(config.sections ?? []).flatMap((section) =>
      section.widgets.map((widget) => widget.instanceId),
    ),
  ];
  const memberships = resolveWidgetSections(blocks, identifiers);
  const bodyId = createId();
  return {
    ...config,
    composition: {
      ...composition,
      blocks: undefined,
      bodies: [
        {
          id: bodyId,
          blocks: blocks.map((block, index) => ({
            ...block,
            ...(block.type === "widgets" ? { widgetInstanceIds: memberships[index] ?? [] } : {}),
            ...(block.type === "event-protection"
              ? { widgetInstanceIds: block.widgetInstanceIds ?? identifiers }
              : {}),
          })),
        },
      ],
      pages: [{ id: createId(), label: "Page 1", bodyId }],
      pagePresentation: { mode: "tabs" },
    },
  };
}

export function createDashboardPage(
  config: DashboardRecord["config"],
  sharedBodyId?: string,
  createId: () => string = () => crypto.randomUUID(),
) {
  const enabled = enableDashboardPages(config, createId);
  const composition = enabled.composition!;
  const bodies = composition.bodies!;
  const shared =
    sharedBodyId === "legacy" && !config.composition?.pages ? bodies[0].id : sharedBodyId;
  if (shared && !bodies.some((body) => body.id === shared))
    throw new Error("Cannot share a missing body");
  const bodyId = shared ?? createId();
  const page: DashboardPage = {
    id: createId(),
    label: `Page ${composition.pages!.length + 1}`,
    bodyId,
  };
  return {
    pageId: page.id,
    config: {
      ...enabled,
      composition: {
        ...composition,
        bodies: shared
          ? bodies
          : [
              ...bodies,
              { id: bodyId, blocks: [{ type: "widgets" as const, widgetInstanceIds: [] }] },
            ],
        pages: [...composition.pages!, page],
      },
    },
  };
}

export function updateDashboardBody(
  composition: DashboardComposition | undefined,
  bodyId: string,
  blocks: DashboardBlock[],
): DashboardComposition {
  if (!composition?.pages) return { ...composition, blocks };
  if (!composition.bodies?.some((body) => body.id === bodyId))
    throw new Error("Cannot edit a missing body");
  return {
    ...composition,
    bodies: composition.bodies.map((body) => (body.id === bodyId ? { ...body, blocks } : body)),
  };
}

export function removeDashboardBody(
  composition: DashboardComposition,
  bodyId: string,
): DashboardComposition {
  if (composition.pages?.some((page) => page.bodyId === bodyId)) {
    throw new Error("Cannot delete a body still referenced by a page");
  }
  return { ...composition, bodies: composition.bodies?.filter((body) => body.id !== bodyId) };
}

/** A widget belongs to one section. New or unassigned widgets stay in the first section. */
export function resolveWidgetSections(
  blocks: NonNullable<DashboardComposition["blocks"]>,
  instanceIds: readonly string[],
  explicit = false,
): Array<string[] | undefined> {
  if (explicit) {
    const available = new Set(instanceIds);
    return blocks.map((block) =>
      block.type === "widgets"
        ? (block.widgetInstanceIds ?? []).filter((id) => available.has(id))
        : undefined,
    );
  }
  const widgetIndexes = blocks.flatMap((block, index) => (block.type === "widgets" ? [index] : []));
  const assigned = new Set<string>();
  const result: Array<string[] | undefined> = blocks.map(() => undefined);
  for (const index of widgetIndexes.slice(1)) {
    result[index] = (blocks[index].widgetInstanceIds ?? []).filter((id) => {
      if (!instanceIds.includes(id) || assigned.has(id)) return false;
      assigned.add(id);
      return true;
    });
  }
  const first = widgetIndexes[0];
  if (first !== undefined) result[first] = instanceIds.filter((id) => !assigned.has(id));
  return result;
}

/**
 * The config as stored. The editor keeps helper fields (a default id and title on the first section, an empty
 * top-level `sections`) that describe nothing the author chose; the API validates the config strictly,
 * so they are left out and only what differs from the defaults is sent.
 */
export function toStoredConfig(config: DashboardRecord["config"]): DashboardRecord["config"] {
  const errors = validateDashboardPages(config);
  if (errors.length) throw new Error(errors.join("; "));
  const { sections, ...rest } = config;
  const stored: DashboardRecord["config"] = sections?.length ? { ...rest, sections } : rest;
  const blocks = config.composition?.blocks;
  if (!blocks) return stored;
  let firstWidgets = true;
  const cleaned = blocks.map((block) => {
    if (block.type !== "widgets") return block;
    const isFirst = firstWidgets;
    firstWidgets = false;
    if (!isFirst) return block;
    const { id, title, widgetInstanceIds, ...others } = block;
    return {
      ...others,
      ...(id && id !== "main" ? { id } : {}),
      ...(title && title !== "Main section" ? { title } : {}),
      ...(widgetInstanceIds ? { widgetInstanceIds } : {}),
    };
  });
  return { ...stored, composition: { ...config.composition, blocks: cleaned } };
}

export function resolveDashboardConfig(config: DashboardRecord["config"]) {
  const composition = config.composition ?? {};
  return {
    ...config,
    scope: config.scope ?? {},
    composition: { ...composition, blocks: composition.blocks ?? widgetBlocks },
  };
}

export function requiresLiveAccess(config: DashboardRecord["config"]): boolean {
  const resolved = resolveDashboardConfig(config);
  return Boolean(
    resolved.composition.context ||
    resolveDashboardPages(resolved.composition).bodies.some((body) =>
      body.blocks.some((block) => block.type !== "widgets"),
    ),
  );
}

export function selectDashboardPage(
  definition: DashboardDefinition,
  composition: DashboardComposition,
  pageId?: string,
) {
  const resolved = resolveDashboardPages(composition);
  const page = resolved.pages.find((candidate) => candidate.id === pageId) ?? resolved.pages[0];
  const body = resolved.bodies.find((candidate) => candidate.id === page?.bodyId);
  if (!page || !body) throw new Error("Dashboard page references a missing body");
  if (!resolved.explicit) {
    return { page, body, explicit: false, definition, links: composition.blockingPeriodSpotlights };
  }
  const identifiers = body.blocks.flatMap((block) => block.widgetInstanceIds ?? []);
  const members = new Set(identifiers);
  return {
    page,
    body,
    explicit: true,
    definition: selectDashboardWidgets(definition, identifiers),
    links: composition.blockingPeriodSpotlights?.filter(
      (link) =>
        members.has(link.spotlightWidgetInstanceId) &&
        link.chartWidgetInstanceIds.every((id) => members.has(id)),
    ),
  };
}

/** Select content without discarding section labels, grouping or responsive layout metadata. */
export function selectDashboardWidgets(
  definition: DashboardDefinition,
  instanceIds?: readonly string[],
): DashboardDefinition {
  if (instanceIds === undefined) return definition;
  const selected = new Set(instanceIds);
  return {
    ...definition,
    widgets: definition.widgets.filter((widget) => selected.has(widget.instanceId)),
    sections: definition.sections
      ?.map((section) => ({
        ...section,
        widgets: section.widgets.filter((widget) => selected.has(widget.instanceId)),
      }))
      .filter((section) => section.widgets.length > 0),
  };
}
